// src/portal-app/more/ProductsScreen.js
//
// Plan 4 Task 4 — Products catalogue screen, migrated to the P4 pattern
// (spec §5). See operations/TransactionsScreen.js:32-55 for the pattern
// this follows; this header only calls out decisions specific to Products.
//
// Hero fields verified against the real response shape before wiring: the
// task brief guessed the screen "already computes `active`/`active_products`"
// fields on the service response — it does not. productService.getAllProducts
// returns a plain array (see services/api.js), no summary object. The hero's
// total/active counts are derived client-side from that array instead.
//
// Status (Active/Inactive) — REVISED after review round 1, then REVISED
// AGAIN after review round 2. This screen originally kept a SegmentedControl
// above the hero, reasoning that Active/Inactive had no meaningful "All"
// state so it didn't fit FilterSheet's optional/clearable idiom. Review
// round 1 (finding 2) overturned the SegmentedControl placement: the
// brief's exception for keeping one above the hero was conditional on it
// switching between genuine sibling views (different services/data shapes/
// row components, e.g. Transactions/Voids); Active/Inactive doesn't qualify
// — same getAllProducts(true) fetch, same array, same CondensedRow — so it
// collapses into FilterSheet like any other categorical field. THAT PART
// STANDS.
//
// Round 1 also changed the default to `null` ("show every status"),
// reasoning that a non-null default would make activeFilterCount misleading
// on first paint. Review round 2 overturned the DEFAULT (not the
// placement): which records show on first paint is behaviour, this plan is
// a presentation rewrite, and the pre-migration screen always defaulted to
// active-only. So `statusFilter` defaults to `'active'`, not `null` — the
// FilterSheet group and its "All" chip still exist and still work exactly
// like round 1 built them; only the initial value changed. A `1` in the
// FilterButton badge on first paint is now considered accurate, not
// misleading: a filter (active-only) genuinely is applied by default.
//
// CondensedRow still carries a status pill (tone + label) on every row —
// once a user picks "All" (or a category-only view happens to include
// both), rows of both statuses can appear in the same list, and the pill
// is how they're told apart. Colour is never the sole carrier of meaning
// (spec §9), so the pill also carries the text label, not just the tone.
//
// Row fields: name, price, category + stock composed into the secondary
// line, status pill. REVISED after review round 1 (finding 3, decided by
// the human): stock is back, cost stays out. A manager scanning the
// catalogue needs the at-a-glance stock signal; cost is a back-office
// number that doesn't earn a scan-time slot. CondensedRow's `secondary`
// prop takes a single string and CondensedRow itself is not touched (three
// other tasks already build on its current contract) — so stock is
// composed into the existing secondary line instead of added as a new
// slot: `${category} · ${stockLabel}` (see stockLabel() below), e.g.
// "Drinks · Low stock". Cost stays dropped — no slot for it, and the human
// ruled it doesn't need one.
//
// Stock fields verified against the real response shape before wiring, the
// same way the hero fields were: `backend/app/models/models.py`'s
// `ProductResponse` (the response_model on `GET /products/`, confirmed at
// `backend/app/routers/products.py:368`) includes `quantity: int`,
// `stock_calculation_type: str`, and `infinite_stock: bool` as real
// top-level fields — these are exactly the fields the pre-migration
// StockBadge component read, not invented ones.
//
// stockLabel() mirrors StockBadge's pre-migration thresholds exactly
// (infinite_stock → unlimited; stock_calculation_type 'auto' is treated
// the same as manual, using `quantity` directly — see the pre-migration
// comment this preserves): qty<=0 → out of stock; qty<=10 → low stock;
// else → in stock. Each state gets distinct WORDING (not just a number),
// because the secondary line has no colour of its own to carry the
// distinction — colour is never the sole carrier of meaning (spec §9).
//
// Category and status both live in FilterSheet — both are real optional/
// clearable filters (their "All" chip always resolves to `null`), alongside
// the fixed active-filter summary + Clear pattern. Category's initial value
// is `null`; status's is `'active'` (see above).
//
// Fixed in passing (same defect TransactionsScreen fixed for its void flow):
// the pre-migration deactivate/reactivate ConfirmSheet usages passed an
// `error` prop. ConfirmSheet (already the shell/kit component even before
// this migration — operations/kit only re-exported it) has never accepted
// one; it was a silent no-op and neither error ever actually rendered.
// ConfirmSheet's real error slot is `children`, rendered inside the sheet
// above the action row — moved both errors there.
//
// Also fixed in passing: the empty-state title and message both used the
// same t() key ('no_products') with only the fallback text differing — once
// that key has a real translation, the title and message would render
// identically. The message now has its own key (no_products_msg).
//
// The list is flattened — no more category section headers. Category is
// now shown per-row as secondary text, so a section header repeated the
// same information the row already carries.
//
// PullToRefresh (Task 2) wraps the count line + list; this screen never had
// its own overflow-y:auto wrapper, so there is nothing to remove there.
//
// Stale-response guard (seqRef): incremented on every load(); callbacks
// compare before committing state — fast retries win over slow initials.
// load() now returns its promise so PullToRefresh can await completion.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { productService, categoryService } from '../../services/api';
import { SurfaceStates, fmtMoney } from '../insights/kit';
import {
    SearchInput,
    ConfirmSheet,
    CondensedRow,
    FilterButton,
    FilterSheet,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
    IconTag,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.prd-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 80px;}

/* ---- hero ---- */
.prd-hero{margin:0 16px 16px;padding:18px 20px 16px;}

/* ---- control row — FilterButton only (no RangeBar equivalent) ---- */
.prd-control-row{display:flex;justify-content:flex-end;padding:0 16px;
  margin-bottom:10px;}

/* ---- active-filter summary ---- */
.prd-filtersum{display:flex;align-items:center;gap:12px;
  margin:4px 16px 12px;}
.prd-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.prd-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- search ---- */
.prd-search-wrap{padding:0 16px;margin-bottom:10px;}

/* ---- count line ---- */
.prd-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}

/* ---- product list ---- */
.prd-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- row wrap — CondensedRow plus the deactivate/reactivate action ---- */
.prd-row-wrap{display:flex;flex-direction:column;gap:6px;}
.prd-action-btn{align-self:flex-start;min-height:44px;padding:0 16px;
  margin-inline-start:44px;border-radius:12px;font-family:inherit;
  font-size:13px;font-weight:700;cursor:pointer;white-space:nowrap;
  transition:transform .12s ease,background .12s ease;}
.prd-action-btn:active{transform:scale(.96);}
.prd-deactivate-btn{
  border:1.5px solid color-mix(in srgb,var(--c-red) 40%,transparent);
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  color:var(--c-red);}
.prd-deactivate-btn:hover{background:color-mix(in srgb,var(--c-red) 14%,transparent);}
.prd-reactivate-btn{
  border:1.5px solid color-mix(in srgb,var(--c-success) 40%,transparent);
  background:color-mix(in srgb,var(--c-success) 8%,transparent);
  color:var(--c-success);}
.prd-reactivate-btn:hover{background:color-mix(in srgb,var(--c-success) 14%,transparent);}

/* ---- filtered-empty inline message (status stays 'ready') ---- */
.prd-filtered-empty{text-align:center;padding:40px 16px;
  color:var(--c-textLight);font-size:14px;font-weight:500;}

/* ---- deactivate/reactivate sheet error — rendered INSIDE the sheet as
   ConfirmSheet children (see file header: error isn't a real prop on
   this component; a sibling would sit behind its fixed backdrop). ---- */
.prd-sheet-error{font-size:12px;font-weight:600;color:var(--c-red);
  margin-top:4px;padding:0 4px;}

/* ---- FAB ---- */
.prd-fab{position:fixed;inset-inline-end:20px;bottom:calc(80px + var(--safe-bottom,0px));
  width:56px;height:56px;border-radius:50%;border:none;
  background:var(--c-green);color:var(--c-white);cursor:pointer;
  box-shadow:0 4px 16px color-mix(in srgb,var(--c-green) 40%,transparent);
  display:inline-flex;align-items:center;justify-content:center;
  transition:transform .15s ease,box-shadow .15s ease;z-index:100;}
.prd-fab:active{transform:scale(.92);}
.prd-fab:hover{box-shadow:0 6px 20px color-mix(in srgb,var(--c-green) 50%,transparent);}

/* ---- create form sheet ---- */
.prd-form-backdrop{position:fixed;inset:0;z-index:200;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.prd-form-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  background:var(--c-surface);
  -webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);}
.prd-form-title{font-size:18px;font-weight:800;color:var(--c-ink);
  margin-bottom:20px;letter-spacing:-.3px;}
.prd-form-group{margin-bottom:14px;}
.prd-form-label{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.7px;color:var(--c-textLight);
  margin-bottom:6px;}
.prd-form-required{color:var(--c-red);margin-inline-start:2px;}
.prd-form-input,.prd-form-select{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;-webkit-appearance:none;appearance:none;}
.prd-form-input:focus,.prd-form-select:focus{border-color:var(--c-green);}
.prd-form-select{cursor:pointer;}
.prd-form-actions{display:flex;gap:10px;margin-top:20px;}
.prd-form-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;color:var(--c-ink);
  cursor:pointer;transition:transform .15s ease;}
.prd-form-cancel:active{transform:scale(.97);}
.prd-form-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  transition:transform .15s ease,opacity .15s ease;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;}
.prd-form-save:active{transform:scale(.97);}
.prd-form-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.prd-form-error{margin-top:10px;font-size:13px;font-weight:600;
  color:var(--c-red);}
.prd-form-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);animation:prdSpin .75s linear infinite;}
@keyframes prdSpin{to{transform:rotate(360deg);}}

/* ---- empty icon ---- */
.prd-empty-icon{display:block;margin:0 auto 8px;}

@media (prefers-reduced-motion:reduce){
  .prd-fab,.prd-fab:active,
  .prd-action-btn,.prd-action-btn:active,
  .prd-form-cancel,.prd-form-cancel:active,
  .prd-form-save,.prd-form-save:active{transition:none;transform:none;}
  .prd-form-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'prd-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Stock label — composed into the row's secondary line alongside       */
/* category (see file header for the field verification + reasoning).   */
/* Mirrors the pre-migration StockBadge thresholds exactly; returns text */
/* only (no colour), so the wording itself must carry the distinction.  */
/* ------------------------------------------------------------------ */
function stockLabel(product, t) {
    if (product.infinite_stock) return t('unlimited', {}, 'Unlimited stock');
    // stock_calculation_type 'auto' uses the same quantity-based read as
    // manual here — see the pre-migration StockBadge comment this repeats.
    const qty = Number(product.quantity) || 0;
    if (qty <= 0) return t('out_of_stock', {}, 'Out of stock');
    if (qty <= 10) return t('stock_low', {}, 'Low stock');
    return t('in_stock', {}, 'In stock');
}

/* ------------------------------------------------------------------ */
/* Product row — CondensedRow plus the deactivate/reactivate action,   */
/* mirroring TransactionsScreen's TransactionRow (CondensedRow + a     */
/* status-dependent action button beneath it, in a shared wrapper that */
/* keeps the pre-migration `product-card-*` testid alive).             */
/* ------------------------------------------------------------------ */
function ProductRow({ product, currency, onDeactivate, onReactivate, t }) {
    const isActive = (product.status || '').toLowerCase() === 'active';
    const tone = isActive ? 'green' : 'muted';
    const secondary = [
        product.category || t('uncategorised', {}, 'Uncategorised'),
        stockLabel(product, t),
    ].filter(Boolean).join(' · ');

    return (
        <div className="prd-row-wrap" data-testid={`product-card-${product.product_code}`}>
            <CondensedRow
                icon={<IconTag />}
                tone={tone}
                name={product.name}
                value={fmtMoney(product.selling_price, currency)}
                secondary={secondary}
                pill={{
                    label: isActive ? t('active', {}, 'Active') : t('inactive', {}, 'Inactive'),
                    tone,
                }}
                testId={product.product_code}
            />
            {isActive ? (
                <button
                    type="button"
                    className="prd-action-btn prd-deactivate-btn"
                    onClick={() => onDeactivate(product)}
                    data-testid={`deactivate-btn-${product.product_code}`}
                    aria-label={`${t('deactivate_product', {}, 'Deactivate')} ${product.name}`}
                >
                    {t('deactivate_product', {}, 'Deactivate')}
                </button>
            ) : (
                <button
                    type="button"
                    className="prd-action-btn prd-reactivate-btn"
                    onClick={() => onReactivate(product)}
                    data-testid={`reactivate-btn-${product.product_code}`}
                    aria-label={`${t('reactivate_product', {}, 'Reactivate')} ${product.name}`}
                >
                    {t('reactivate_product', {}, 'Reactivate')}
                </button>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Create product form (glass bottom sheet) — unchanged by this task   */
/* ------------------------------------------------------------------ */
function CreateProductForm({ categories, currency, onSave, onCancel, t }) {
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [subcategory, setSubcategory] = useState('');
    const [sellingPrice, setSellingPrice] = useState('');
    const [costPrice, setCostPrice] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleSave = async () => {
        if (!name.trim() || !category || !sellingPrice) return;
        setSaving(true);
        setError(null);
        try {
            await onSave({
                name: name.trim(),
                product_code: '',
                category,
                subcategory: subcategory.trim() || undefined,
                selling_price: parseFloat(sellingPrice),
                cost_price: costPrice ? parseFloat(costPrice) : undefined,
                description: description.trim() || undefined,
            });
        } catch (err) {
            setSaving(false);
            setError(t('product_create_error', {}, 'Failed to create product. Please try again.'));
        }
    };

    return (
        <div
            className="prd-form-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
            role="presentation"
        >
            <div
                className="prd-form-sheet"
                data-testid="create-product-form"
                role="dialog"
                aria-modal="true"
                aria-label={t('add_product', {}, 'Add Product')}
            >
                <div className="prd-form-title">{t('add_product', {}, 'Add Product')}</div>

                {/* Name */}
                <div className="prd-form-group">
                    <label className="prd-form-label" htmlFor="prd-create-name">
                        {t('product_name', {}, 'Name')}
                        <span className="prd-form-required" aria-hidden="true"> *</span>
                    </label>
                    <input
                        id="prd-create-name"
                        className="prd-form-input"
                        data-testid="create-name-input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('product_name', {}, 'Product name')}
                        autoFocus
                    />
                </div>

                {/* Category */}
                <div className="prd-form-group">
                    <label className="prd-form-label" htmlFor="prd-create-cat">
                        {t('expense_category', {}, 'Category')}
                        <span className="prd-form-required" aria-hidden="true"> *</span>
                    </label>
                    <select
                        id="prd-create-cat"
                        className="prd-form-select"
                        data-testid="create-category-select"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <option value="">{t('select_category', {}, 'Select category…')}</option>
                        {categories.map((c) => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {/* Subcategory */}
                <div className="prd-form-group">
                    <label className="prd-form-label" htmlFor="prd-create-subcat">
                        {t('product_subcategory', {}, 'Subcategory')} <span style={{ fontWeight: 400 }}>({t('optional', {}, 'optional')})</span>
                    </label>
                    <input
                        id="prd-create-subcat"
                        className="prd-form-input"
                        data-testid="create-subcategory-input"
                        type="text"
                        value={subcategory}
                        onChange={(e) => setSubcategory(e.target.value)}
                        placeholder={t('optional', {}, 'optional')}
                    />
                </div>

                {/* Selling price */}
                <div className="prd-form-group">
                    <label className="prd-form-label" htmlFor="prd-create-price">
                        {t('selling_price', {}, 'Selling Price')}
                        <span className="prd-form-required" aria-hidden="true"> *</span>
                    </label>
                    <input
                        id="prd-create-price"
                        className="prd-form-input"
                        data-testid="create-price-input"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={sellingPrice}
                        onChange={(e) => setSellingPrice(e.target.value)}
                        placeholder="0.00"
                    />
                </div>

                {/* Cost price */}
                <div className="prd-form-group">
                    <label className="prd-form-label" htmlFor="prd-create-cost">
                        {t('cost_price', {}, 'Cost Price')} <span style={{ fontWeight: 400 }}>({t('optional', {}, 'optional')})</span>
                    </label>
                    <input
                        id="prd-create-cost"
                        className="prd-form-input"
                        data-testid="create-cost-input"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={costPrice}
                        onChange={(e) => setCostPrice(e.target.value)}
                        placeholder="0.00"
                    />
                </div>

                {/* Description */}
                <div className="prd-form-group">
                    <label className="prd-form-label" htmlFor="prd-create-desc">
                        {t('product_description', {}, 'Description')} <span style={{ fontWeight: 400 }}>({t('optional', {}, 'optional')})</span>
                    </label>
                    <input
                        id="prd-create-desc"
                        className="prd-form-input"
                        data-testid="create-desc-input"
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t('optional', {}, 'optional')}
                    />
                </div>

                {error && <div className="prd-form-error">{error}</div>}

                <div className="prd-form-actions">
                    <button
                        type="button"
                        className="prd-form-cancel"
                        onClick={onCancel}
                        data-testid="create-cancel-btn"
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="prd-form-save"
                        onClick={handleSave}
                        data-testid="create-save-btn"
                        disabled={saving || !name.trim() || !category || !sellingPrice}
                    >
                        {saving && <span className="prd-form-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* ProductsScreen                                                      */
/* ------------------------------------------------------------------ */
export default function ProductsScreen() {
    ensureCss();
    ensureKitCss();
    const { t } = useLanguage();
    const { getCurrency } = useAuth();
    const currency = getCurrency();

    // Data state
    const [status, setStatus] = useState('loading');
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    // Filter state — category defaults to null ("All"), same idiom as every
    // other FilterSheet group. Status defaults to 'active' (see file
    // header, review round 2): default *records shown* is behaviour, not
    // presentation, and the pre-migration screen always opened active-only.
    // The FilterSheet group and its "All" chip still resolve to `null`
    // (show both) when picked — only the initial value differs.
    const [statusFilter, setStatusFilter] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    // Deactivate sheet state
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [deactivateReason, setDeactivateReason] = useState('');
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [deactivateError, setDeactivateError] = useState(null);

    // Reactivate sheet state
    const [reactivateTarget, setReactivateTarget] = useState(null);
    const [reactivateLoading, setReactivateLoading] = useState(false);
    const [reactivateError, setReactivateError] = useState(null);

    // Create form state
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Stale-response guard
    const seqRef = useRef(0);

    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');

        return Promise.all([
            productService.getAllProducts(true),
            categoryService.getAll().catch(() => []),
        ]).then(([productList, categoryList]) => {
            if (seq !== seqRef.current) return; // stale
            const list = Array.isArray(productList) ? productList : [];
            setProducts(list);
            setCategories(Array.isArray(categoryList) ? categoryList : []);
            setStatus(list.length === 0 ? 'empty' : 'ready');
        }).catch(() => {
            if (seq !== seqRef.current) return;
            setStatus('error');
        });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    /* ------ derived: hero (whole catalogue, unaffected by filters) ---- */
    const totalCount = products.length;
    const activeCount = products.filter((p) => (p.status || '').toLowerCase() === 'active').length;

    /* ------ derived: filtered + flattened list ------------------------ */
    const filteredProducts = products.filter((p) => {
        const productStatus = (p.status || '').toLowerCase();
        if (statusFilter && productStatus !== statusFilter) return false;
        if (categoryFilter && p.category !== categoryFilter) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!(p.name || '').toLowerCase().includes(q)) return false;
        }
        return true;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    /* ------ derived: FilterSheet groups + active-filter summary ------- */
    const statusOptions = [
        { id: 'active', label: t('active_products', {}, 'Active') },
        { id: 'inactive', label: t('inactive_products', {}, 'Inactive') },
    ];
    const categoryOptions = categories.map((c) => ({ id: c.name, label: c.name }));
    const filterGroups = [
        { id: 'status', label: t('status', {}, 'Status'), options: statusOptions },
        ...(categoryOptions.length > 0
            ? [{ id: 'category', label: t('expense_category', {}, 'Category'), options: categoryOptions }]
            : []),
    ];
    const filterValue = { status: statusFilter, category: categoryFilter };
    const activeFilterCount = (statusFilter ? 1 : 0) + (categoryFilter ? 1 : 0);

    const statusFilterLabel = statusFilter === 'active'
        ? t('active_products', {}, 'Active')
        : statusFilter === 'inactive'
            ? t('inactive_products', {}, 'Inactive')
            : null;
    const activeFilterText = [statusFilterLabel, categoryFilter].filter(Boolean).join(' · ');

    const handleFilterChange = (groupId, optionId) => {
        if (groupId === 'status') setStatusFilter(optionId);
        else if (groupId === 'category') setCategoryFilter(optionId);
    };

    const clearAllFilters = () => {
        setStatusFilter(null);
        setCategoryFilter(null);
        setSearchQuery('');
    };

    /* ------ handlers ---------------------------------------------------*/
    const handleDeactivate = (product) => {
        setDeactivateTarget(product);
        setDeactivateReason('');
        setDeactivateError(null);
    };

    const handleDeactivateConfirm = async () => {
        if (!deactivateTarget) return;
        setDeactivateLoading(true);
        setDeactivateError(null);
        try {
            await productService.deactivateProduct(deactivateTarget.product_code, deactivateReason || null);
            setDeactivateTarget(null);
            setDeactivateLoading(false);
            load();
        } catch {
            setDeactivateLoading(false);
            setDeactivateError(t('product_deactivate_error', {}, 'Failed to deactivate product. Please try again.'));
        }
    };

    const handleDeactivateClose = () => {
        setDeactivateTarget(null);
        setDeactivateError(null);
    };

    const handleReactivate = (product) => {
        setReactivateTarget(product);
        setReactivateError(null);
    };

    const handleReactivateConfirm = async () => {
        if (!reactivateTarget) return;
        setReactivateLoading(true);
        setReactivateError(null);
        try {
            await productService.reactivateProduct(reactivateTarget.product_code);
            setReactivateTarget(null);
            setReactivateLoading(false);
            load();
        } catch {
            setReactivateLoading(false);
            setReactivateError(t('product_reactivate_error', {}, 'Failed to reactivate product. Please try again.'));
        }
    };

    const handleReactivateClose = () => {
        setReactivateTarget(null);
        setReactivateError(null);
    };

    const handleCreate = async (data) => {
        await productService.createProduct(data);
        setShowCreateForm(false);
        load();
    };

    const ptrDisabled = filterSheetOpen || showCreateForm || !!deactivateTarget || !!reactivateTarget;

    return (
        <div className="prd-wrap">
            {/* Hero card — whole-catalogue summary, OUTSIDE SurfaceStates so it
                never vanishes. Unaffected by status/category/search filters. */}
            <GlassCard veil className="prd-hero" data-testid="products-hero">
                <HeroStat
                    label={t('total_products', {}, 'Total Products')}
                    value={totalCount}
                    caption={t('n_active_products', { count: activeCount }, '{count} active')}
                />
            </GlassCard>

            {/* Control row — FilterButton only; no RangeBar equivalent here. */}
            <div className="prd-control-row">
                <FilterButton
                    activeCount={activeFilterCount}
                    onPress={() => setFilterSheetOpen(true)}
                />
            </div>

            {/* Active-filter summary — OUTSIDE SurfaceStates so a filtered-to-
                empty result still shows the means to clear the filter. */}
            {activeFilterCount > 0 && (
                <div className="prd-filtersum">
                    <span className="prd-filtersum-text">{activeFilterText}</span>
                    <button
                        type="button"
                        className="prd-filtersum-clear"
                        data-testid="clear-filters"
                        onClick={clearAllFilters}
                    >
                        {t('clear', {}, 'Clear')}
                    </button>
                </div>
            )}

            {/* Search — OUTSIDE SurfaceStates, alongside the other controls, for
                the same reason as Transactions: a term matching nothing must not
                unmount the field mid-keystroke. */}
            <div className="prd-search-wrap">
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={t('search', {}, 'Search products…')}
                />
            </div>

            <PullToRefresh onRefresh={() => load()} disabled={ptrDisabled}>
                <SurfaceStates
                    status={status}
                    onRetry={load}
                    emptyTitle={t('no_products', {}, 'No products found')}
                    emptyMsg={t('no_products_msg', {}, 'Add a product using the + button below.')}
                    emptyIcon={
                        <svg
                            className="prd-empty-icon"
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <path d="M16 10a4 4 0 01-8 0" />
                        </svg>
                    }
                >
                    {/* Count line */}
                    <div className="prd-count-line" data-testid="products-count-line">
                        {t('n_products', { count: filteredProducts.length }, '{count} Products')}
                    </div>

                    {/* Flat product list — category now shown per-row (secondary
                        text), so a repeated section header would duplicate it. */}
                    {filteredProducts.length > 0 ? (
                        <div className="prd-list">
                            {filteredProducts.map((product) => (
                                <ProductRow
                                    key={product.product_code}
                                    product={product}
                                    currency={currency}
                                    onDeactivate={handleDeactivate}
                                    onReactivate={handleReactivate}
                                    t={t}
                                />
                            ))}
                        </div>
                    ) : (
                        // Filtering client-side never flips `status` away from
                        // 'ready' (the raw fetch still succeeded with items) — so
                        // a filter/search producing zero matches renders this
                        // inline message inside the ready branch, same as before.
                        <div className="prd-filtered-empty">
                            {t('no_products_filter_msg', {}, 'No products match your filters.')}
                        </div>
                    )}
                </SurfaceStates>
            </PullToRefresh>

            {/* ── FAB ── */}
            <button
                type="button"
                className="prd-fab"
                onClick={() => setShowCreateForm(true)}
                data-testid="fab-add-product"
                aria-label={t('add_product', {}, 'Add product')}
            >
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            </button>

            {/* ── deactivate confirm sheet ── */}
            <ConfirmSheet
                open={!!deactivateTarget}
                onClose={handleDeactivateClose}
                onConfirm={handleDeactivateConfirm}
                title={t('deactivate_product', {}, 'Deactivate Product')}
                message={t('deactivate_confirm', {}, 'This product will be hidden from the POS and cannot be sold.')}
                confirmLabel={t('deactivate_product', {}, 'Deactivate')}
                destructive
                loading={deactivateLoading}
                reasonLabel={t('reason_optional', {}, 'Reason (optional)')}
                reasonValue={deactivateReason}
                onReasonChange={setDeactivateReason}
            >
                {deactivateError && (
                    <div className="prd-sheet-error" data-testid="deactivate-error">
                        {deactivateError}
                    </div>
                )}
            </ConfirmSheet>

            {/* ── reactivate confirm sheet ── */}
            <ConfirmSheet
                open={!!reactivateTarget}
                onClose={handleReactivateClose}
                onConfirm={handleReactivateConfirm}
                title={t('reactivate_product', {}, 'Reactivate Product')}
                message={t('reactivate_confirm', {}, 'This product will be visible on the POS again.')}
                confirmLabel={t('reactivate_product', {}, 'Reactivate')}
                loading={reactivateLoading}
            >
                {reactivateError && (
                    <div className="prd-sheet-error" data-testid="reactivate-error">
                        {reactivateError}
                    </div>
                )}
            </ConfirmSheet>

            {/* ── create form ── */}
            {showCreateForm && (
                <CreateProductForm
                    categories={categories}
                    currency={currency}
                    onSave={handleCreate}
                    onCancel={() => setShowCreateForm(false)}
                    t={t}
                />
            )}

            {/* ── filter sheet — status + category ── */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
